import { SequenceReplayWindow } from '@spectrabus/protocol';
import type { Direction, PhysicalChannel } from '@spectrabus/protocol';

/** PHY profile labels bound into every authenticated frame envelope. */
export const OPTICAL_PROFILE_ID = 'O1-BINARY';
export const ACOUSTIC_FORWARD_PROFILE_ID = 'A1-FDD-FWD';
export const ACOUSTIC_RETURN_PROFILE_ID = 'A1-FDD-RET';

/**
 * The live path binds the session identifier into the fountain seed, unlike
 * the reproducible demo scenario. Two live sessions moving identical bytes
 * must not share coefficient vectors; both peers derive this string from the
 * handshake session id and the manifest object hash without negotiation.
 */
export function liveFountainSeed(sessionId: string, objectHash: string): string {
  return `spectrabus-live-v1:${sessionId}:${objectHash}`;
}

export function manifestContextId(transferId: string): string {
  return `manifest:${transferId}`;
}

const BASE64_URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function base64UrlEncode(bytes: Uint8Array): string {
  let encoded = '';
  for (let offset = 0; offset < bytes.length; offset += 3) {
    const first = bytes[offset] ?? 0;
    const second = bytes[offset + 1] ?? 0;
    const third = bytes[offset + 2] ?? 0;
    encoded += BASE64_URL_ALPHABET.charAt(first >>> 2);
    encoded += BASE64_URL_ALPHABET.charAt(((first & 0x03) << 4) | (second >>> 4));
    if (offset + 1 < bytes.length) {
      encoded += BASE64_URL_ALPHABET.charAt(((second & 0x0f) << 2) | (third >>> 6));
    }
    if (offset + 2 < bytes.length) {
      encoded += BASE64_URL_ALPHABET.charAt(third & 0x3f);
    }
  }
  return encoded;
}

/** The QR bootstrap text for a discovery offer, matching the scanner. */
export function encodeOfferText(offerBytes: Uint8Array): string {
  return `SB1:${base64UrlEncode(offerBytes)}`;
}

export function feedbackContextId(transferId: string): string {
  return `feedback:${transferId}`;
}

/**
 * Replay windows are sized for two physical channels interleaving one
 * emission stream each. The historical 64-entry window assumed loopback
 * delivery; a physical pair sees larger per-channel gaps whenever one
 * channel outruns the other, so the live engines track a wider horizon.
 */
export const LIVE_REPLAY_WINDOW = 256;

export type StreamKey = `${Direction}:${PhysicalChannel}`;

export function streamKey(direction: Direction, channel: PhysicalChannel): StreamKey {
  return `${direction}:${channel}`;
}

/** Independent monotonic sequence spaces per direction and channel. */
export class SequenceAllocator {
  #next = new Map<StreamKey, number>();

  public next(direction: Direction, channel: PhysicalChannel): number {
    const key = streamKey(direction, channel);
    const value = this.#next.get(key) ?? 1;
    this.#next.set(key, value + 1);
    return value;
  }
}

export class ReplayWindows {
  #windows = new Map<StreamKey, SequenceReplayWindow>();

  public for(direction: Direction, channel: PhysicalChannel): SequenceReplayWindow {
    const key = streamKey(direction, channel);
    let window = this.#windows.get(key);
    if (window === undefined) {
      window = new SequenceReplayWindow(LIVE_REPLAY_WINDOW);
      this.#windows.set(key, window);
    }
    return window;
  }

  public get size(): number {
    return this.#windows.size;
  }
}

export interface LiveObjectInput {
  readonly bytes: Uint8Array;
  readonly name: string;
  readonly mimeType: string;
}

export type LiveEventKind =
  | 'response-accepted'
  | 'confirmation-verified'
  | 'confirmation-duplicate'
  | 'manifest-progress'
  | 'manifest-verified'
  | 'equation-admitted'
  | 'equation-duplicate'
  | 'generation-verified'
  | 'feedback-received'
  | 'object-verified'
  | 'phase-changed'
  | 'rejected';

export interface LiveEvent {
  readonly kind: LiveEventKind;
  readonly channel: PhysicalChannel | null;
  readonly detail: string;
}

export function liveEvent(
  kind: LiveEventKind,
  channel: PhysicalChannel | null,
  detail: string,
): LiveEvent {
  return { kind, channel, detail };
}
